import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { CopyableCode } from '../../components/ui/CopyableCode';
import { Markdown } from '../../components/ui/Markdown';
import { TableTools } from '../../components/ui/TableTools';
import { num } from '../../format';
import { useAuth } from '../auth/AuthContext';
import { useNavigation } from '../layout/navigation';
import { downloadBlob } from '../labels/api';
import { listSpecs } from '../specs/api';
import {
  addItem,
  archiveProject,
  restoreProject,
  deleteItem,
  demandXlsx,
  draftPurchase,
  getDemand,
  getProject,
  issueKit,
  reserveAvailable,
  setReservation,
  updateItem,
  updateProject,
  PROJECT_STATUSES,
  type DemandRow,
  type FulfillmentOption,
  type ProjectStatus,
} from './api';
import { daysUntilDue } from './due';

interface Props {
  projectId: string;
  onArchived: () => void;
}

/**
 * A project: what is planned, and what that means for the shelves.
 *
 * The shortage table is the point of the screen, so it is not behind a tab. Its
 * three columns say the same thing three ways — needed, already taken, on the
 * shelf — because "buy 40" is only believable next to the numbers it came from.
 */
export function ProjectPanel({ projectId, onArchived }: Props) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigation = useNavigation();
  const queryClient = useQueryClient();

  const [includeOptional, setIncludeOptional] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [selectedReservations, setSelectedReservations] = useState<Set<string>>(new Set());

  const canWrite = can('project.write');
  const canIssue = can('stock.post');
  const canBuy = can('purchase.write');

  const project = useQuery({
    queryKey: ['projects', projectId],
    queryFn: () => getProject(projectId),
  });

  const demand = useQuery({
    queryKey: ['projects', projectId, 'demand', includeOptional],
    queryFn: () => getDemand(projectId, includeOptional),
  });

  const fail = (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown');

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
    // Issuing moves stock, so the catalogue's on-hand figures moved with it.
    void queryClient.invalidateQueries({ queryKey: ['components'] });
    void queryClient.invalidateQueries({ queryKey: ['stock'] });
  }

  const setStatus = useMutation({
    mutationFn: (status: ProjectStatus) => updateProject(projectId, { status }),
    onSuccess: refresh,
    onError: fail,
  });

  const restore = useMutation({
    mutationFn: () => restoreProject(projectId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });

  const archive = useMutation({
    mutationFn: () => archiveProject(projectId),
    onSuccess: () => {
      refresh();
      onArchived();
    },
    onError: fail,
  });

  const drop = useMutation({
    mutationFn: (itemId: string) => deleteItem(projectId, itemId),
    onSuccess: refresh,
    onError: fail,
  });

  const setUnits = useMutation({
    mutationFn: (input: { itemId: string; units: number }) =>
      updateItem(projectId, input.itemId, { units: input.units }),
    onSuccess: refresh,
    onError: fail,
  });

  const order = useMutation({
    mutationFn: () => draftPurchase(projectId, includeOptional),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['purchases'] });
    },
    onError: fail,
  });

  const reserveSelected = useMutation({
    mutationFn: () => reserveAvailable(projectId, [...selectedReservations]),
    onSuccess: () => {
      setError(null);
      setSelectedReservations(new Set());
      refresh();
    },
    onError: fail,
  });

  if (project.isLoading || !project.data) {
    return <p className="panel__hint">{t('common.loading')}</p>;
  }

  const item = project.data;
  const rows = demand.data?.rows ?? [];
  const daysLeft = item.due_at ? daysUntilDue(item.due_at) : null;

  return (
    <article className="panel">
      <header className="panel__head">
        <div>
          <h2>{item.name}</h2>
          <p className="panel__sub">
            {item.archived_at && (
              <span className="badge badge--warn">{t('common.archived')}</span>
            )}
            <CopyableCode value={item.code} className="components__code" label={t('projects.code')} />
            <span className={`purchase__status purchase__status--${item.status}`}>
              {t(`projects.status.${item.status}`)}
            </span>
            {item.due_at && (
              <span className="stock__when">
                {t('projects.dueAt', { when: new Date(`${item.due_at}T00:00:00`).toLocaleDateString() })}
                {' · '}
                {daysLeft !== null && daysLeft >= 0
                  ? t('projects.daysRemaining', { n: daysLeft })
                  : t('projects.daysOverdue', { n: Math.abs(daysLeft ?? 0) })}
              </span>
            )}
          </p>
          {/* Both, and both formatted. The note is what the person who started
              the project typed into the only free-text field the form offers,
              and until now the card never showed it back to them. */}
          {item.description && (
            <div className="panel__description">
              <Markdown source={item.description} lines={3} />
            </div>
          )}
          {item.note && (
            <div className="panel__description">
              <Markdown source={item.note} lines={3} />
            </div>
          )}
        </div>
        {canWrite && item.archived_at && (
          <div className="panel__actions">
            {/* Archiving destroyed nothing, so the way back is one press. */}
            <button type="button" onClick={() => restore.mutate()} disabled={restore.isPending}>
              {t('common.restore')}
            </button>
          </div>
        )}
        {canWrite && !item.archived_at && (
          <div className="panel__actions">
            <select
              value={item.status}
              onChange={(event) => setStatus.mutate(event.target.value as ProjectStatus)}
            >
              {PROJECT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(`projects.status.${value}`)}
                </option>
              ))}
            </select>
            <button type="button" className="danger" onClick={() => archive.mutate()}>
              {t('projects.archive')}
            </button>
          </div>
        )}
      </header>

      {error && (
        <p className="panel__error">
          {t(`projects.errors.${error}`, t('projects.errors.unknown'))}
        </p>
      )}

      <section>
        <div className="panel__section-head">
          <h3>{t('projects.plan')}</h3>
          {canWrite && (
            <button type="button" onClick={() => setAdding((open) => !open)}>
              {t('projects.addItem')}
            </button>
          )}
        </div>

        {item.items.length === 0 ? (
          <p className="panel__hint">{t('projects.noItems')}</p>
        ) : (
          <ul className="project__items">
            {item.items.map((row) => (
              <li key={row.id}>
                <span className="project__item-name">
                  {row.spec_version && navigation.canOpen('spec') ? (
                    <button
                      type="button"
                      className="link"
                      onClick={() => navigation.open('spec', row.spec_version!.specification_id)}
                    >
                      {row.specification_name ?? row.spec_version.code}
                    </button>
                  ) : (
                    <span>{row.specification_name ?? row.spec_version?.code}</span>
                  )}
                  <em>
                    {' '}
                    {t('specs.versionShort', { version: row.spec_version?.version })} ·{' '}
                    {row.spec_version?.code}
                  </em>
                </span>
                {canWrite ? (
                  <input
                    type="number"
                    min={1}
                    defaultValue={row.units}
                    onBlur={(event) => {
                      const units = Number(event.target.value);
                      if (units > 0 && units !== row.units) {
                        setUnits.mutate({ itemId: row.id, units });
                      }
                    }}
                    aria-label={t('projects.units')}
                  />
                ) : (
                  <span>{row.units}</span>
                )}
                <span className="project__units-label">{t('projects.units')}</span>
                {canWrite && (
                  <button type="button" className="danger" onClick={() => drop.mutate(row.id)}>
                    {t('common.delete')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {adding && canWrite && (
          <AddItem
            projectId={projectId}
            onAdded={() => {
              setAdding(false);
              refresh();
            }}
          />
        )}
      </section>

      <section className="project__demand">
        <div className="panel__section-head">
          <h3>
            {t('projects.demand')}
            {demand.data && demand.data.short_count > 0 && (
              <span className="project__short-count">
                {t('projects.shortCount', { n: demand.data.short_count })}
              </span>
            )}
          </h3>
          <label className="spec__optional">
            <input
              type="checkbox"
              checked={includeOptional}
              onChange={(event) => setIncludeOptional(event.target.checked)}
            />
            {t('projects.includeOptional')}
          </label>
          {canBuy && (
            <button
              type="button"
              disabled={order.isPending || (demand.data?.short_count ?? 0) === 0}
              onClick={() => order.mutate()}
            >
              {t('projects.orderShortage')}
            </button>
          )}
          {canWrite && selectedReservations.size > 0 && (
            <button
              type="button"
              disabled={reserveSelected.isPending}
              onClick={() => {
                if (window.confirm(t('projects.confirmReserveAvailable', { n: selectedReservations.size }))) {
                  reserveSelected.mutate();
                }
              }}
            >
              {t('projects.reserveAvailable', { n: selectedReservations.size })}
            </button>
          )}
          {/* Offered only once there is a table to take away — a button that
              writes an empty file is a button that wasted a click. */}
          <TableTools
            label={t('projects.demand')}
            onExport={
              rows.length > 0
                ? () =>
                    void demandXlsx(projectId, includeOptional).then((blob) =>
                      downloadBlob(blob, `demand-${project.data?.code ?? projectId}.xlsx`),
                    )
                : undefined
            }
          />
        </div>

        {order.isSuccess && (
          <p className="panel__hint">
            {t('projects.orderCreated', { code: order.data.code })}
          </p>
        )}

        {demand.isLoading && <p className="panel__hint">{t('common.loading')}</p>}
        {demand.data && rows.length === 0 && <p className="panel__hint">{t('projects.noDemand')}</p>}

        {rows.length > 0 && (
          <div className="scroll-x">
            <table className="project__table">
              <colgroup>
                <col className="project__column--component" />
                <col className="project__column--required" />
                <col className="project__column--coverage" />
                <col className="project__column--reserve" />
                <col className="project__column--issue" />
              </colgroup>
              <thead>
                <tr>
                  <th>{t('projects.part')}</th>
                  <th>{t('projects.required')}</th>
                  <th>{t('projects.coverage')}</th>
                  <th>{t('projects.reserve')}</th>
                  <th>{t('projects.issue')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <DemandLine
                    key={row.component_id}
                    projectId={projectId}
                    dueAt={item.due_at}
                    row={row}
                    canReserve={canWrite}
                    selected={selectedReservations.has(row.component_id)}
                    onSelect={(selected) =>
                      setSelectedReservations((current) => {
                        const next = new Set(current);
                        if (selected) next.add(row.component_id);
                        else next.delete(row.component_id);
                        return next;
                      })
                    }
                    canIssue={canIssue}
                    onIssued={() => {
                      refresh();
                      void queryClient.invalidateQueries({
                        queryKey: ['projects', projectId, 'demand'],
                      });
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </article>
  );
}

/** One part of the report, with the shelf it can be taken from already filled
 *  in — the report knows where most of it sits, so issuing is a press rather
 *  than a search. */
function DemandLine({
  projectId,
  dueAt,
  row,
  canIssue,
  canReserve,
  selected,
  onSelect,
  onIssued,
}: {
  projectId: string;
  dueAt: string | null;
  row: DemandRow;
  canIssue: boolean;
  canReserve: boolean;
  selected: boolean;
  onSelect: (selected: boolean) => void;
  onIssued: () => void;
}) {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const short = Number(row.shortage) > 0;

  return (
    <>
      <tr className={short ? 'project__row--short' : undefined}>
        <td className="project__component">
          {navigation.canOpen('component') ? (
            <button type="button" className="link project__component-link" onClick={() => navigation.open('component', row.component_id)}>
              {row.name}
            </button>
          ) : <strong>{row.name}</strong>}
          <em className="components__code">{row.internal_code}</em>
        </td>
        <td><div className="project__numbers"><span>{t('projects.required')}</span><strong>{num(row.required)}</strong><span>{t('projects.issued')}</span><strong>{num(row.issued)}</strong></div></td>
        <td>
          <div className="project__coverage">
            <span>{row.fulfillment_options.length > 1 ? t('projects.onHandWithAlternatives') : t('projects.onHand')}</span><strong>{num(row.on_hand)}</strong>
            {Number(row.ordered) > 0 && <><span>{t('projects.ordered')}</span><strong>{num(row.ordered)}</strong></>}
            {Number(row.timely_expected) > 0 && <><span>{t('projects.expectedCovered')}</span><strong>{num(row.timely_expected)}</strong></>}
            {Number(row.draft_quantity) > 0 && <><span>{t('projects.draftProposals')}</span><strong>{num(row.draft_quantity)}</strong></>}
            {Number(row.reserved_elsewhere) > 0 && <><span>{t('projects.reservedElsewhere')}</span><strong>{num(row.reserved_elsewhere)}</strong></>}
            <span>{t('projects.free')}</span><strong>{num(row.free)}</strong>
            {short && <><span className="project__shortage">{t('projects.shortage')}</span><strong className="project__shortage">{num(row.shortage)}</strong></>}
          </div>
          {row.deliveries.length > 0 && <div className="project__deliveries">{row.deliveries.map((delivery) => <span key={`${delivery.purchase_line_id}-${delivery.purchase_id}`}>{delivery.purchase_code}: {num(delivery.quantity)} · {delivery.expected_at ?? t('projects.unknownDate')}{dueAt && delivery.expected_at && delivery.expected_at > dueAt ? ` · ${t('projects.late')}` : ''}</span>)}</div>}
          {row.draft_deliveries.length > 0 && <div className="project__deliveries project__deliveries--draft">{row.draft_deliveries.map((delivery) => <span key={`${delivery.purchase_line_id}-${delivery.purchase_id}`}>{t('projects.draftProposal')}: {delivery.purchase_code} · {num(delivery.quantity)}</span>)}</div>}
        </td>
        <td>
          <span className="project__reserved-value">{t('projects.reserved')}: <strong>{num(row.reserved)}</strong></span>
          {canReserve && <label className="stock__when project__reserve-select"><input type="checkbox" checked={selected} onChange={(event) => onSelect(event.target.checked)} />{t('projects.selectForReserve')}</label>}
        </td>
        <td />
      </tr>
      <tr className="project__options-row">
        <td colSpan={5}>
          <div className="project__fulfillment-options">
            <h4>{t('projects.fulfillmentOptions')}</h4>
            {row.fulfillment_options.map((option, index) => (
              <FulfillmentOptionLine key={option.component_id} projectId={projectId} row={row} option={option} primary={index === 0} canReserve={canReserve} canIssue={canIssue} onIssued={onIssued} />
            ))}
          </div>
        </td>
      </tr>
    </>
  );
}

function FulfillmentOptionLine({ projectId, row, option, primary, canReserve, canIssue, onIssued }: { projectId: string; row: DemandRow; option: FulfillmentOption; primary: boolean; canReserve: boolean; canIssue: boolean; onIssued: () => void }) {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const outstanding = Math.max(0, Number(row.required) - Number(row.issued));
  const ceiling = Math.max(0, outstanding - (Number(row.reserved) - Number(option.reserved)));
  const [reservationQuantity, setReservationQuantity] = useState(num(option.reserved));
  const [quantity, setQuantity] = useState(String(Math.min(outstanding, Number(option.reserved)) || ''));
  const [issueKey, setIssueKey] = useState(() => crypto.randomUUID());
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setReservationQuantity(num(option.reserved)), [option.reserved]);
  useEffect(() => setQuantity(String(Math.min(outstanding, Number(option.reserved)) || '')), [outstanding, option.reserved]);

  const reserve = useMutation({
    mutationFn: () => setReservation(projectId, { component_id: option.component_id, requirement_component_id: row.component_id, quantity: Number(reservationQuantity) > 0 ? reservationQuantity : String(ceiling) }),
    onSuccess: onIssued,
    onError: (exc) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });
  const release = useMutation({
    mutationFn: () => setReservation(projectId, { component_id: option.component_id, requirement_component_id: row.component_id, quantity: '0' }),
    onSuccess: onIssued,
    onError: (exc) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });
  const issue = useMutation({
    mutationFn: () => issueKit(projectId, [{ component_id: option.component_id, location_id: option.suggested_location_id as string, quantity }], undefined, issueKey),
    onSuccess: () => { setError(null); setIssueKey(crypto.randomUUID()); onIssued(); },
    onError: (exc) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  return (
    <div className="project__fulfillment-option">
      <div className="project__option-component">
        <span className="badge">{t(primary ? 'projects.primary' : 'projects.alternative')}</span>
        {navigation.canOpen('component') ? <button type="button" className="link project__option-component-link" onClick={() => navigation.open('component', option.component_id)}>{option.name}</button> : <strong>{option.name}</strong>}
        {navigation.canOpen('component') ? <button type="button" className="link components__code project__option-component-code" onClick={() => navigation.open('component', option.component_id)}>{option.internal_code}</button> : <em className="components__code">{option.internal_code}</em>}
      </div>
      <div className="project__option-stock"><span>{t('projects.onHand')}</span><strong>{num(option.on_hand)}</strong><span>{t('projects.free')}</span><strong>{num(option.free)}</strong><span>{t('projects.reserved')}</span><strong>{num(option.reserved)}</strong></div>
      {canReserve && <div className="project__option-actions"><input type="number" className="project__quantity-input" min="0" max={num(ceiling)} step="any" value={reservationQuantity} onChange={(event) => setReservationQuantity(event.target.value)} aria-label={t('projects.reserveQuantity')} /><button type="button" disabled={reserve.isPending || Number(reservationQuantity) < 0 || ceiling <= 0} onClick={() => reserve.mutate()}>{t('projects.reserve')}</button>{Number(option.reserved) > 0 && <button type="button" className="link" disabled={release.isPending} onClick={() => release.mutate()}>{t('projects.releaseReserve')}</button>}</div>}
      {canIssue && option.suggested_location_id && Number(option.reserved) > 0 && <div className="project__option-actions"><em className="stock__when">{option.suggested_location_name}</em><input className="project__quantity-input" value={quantity} onChange={(event) => setQuantity(event.target.value)} inputMode="decimal" aria-label={t('projects.issueQuantity')} /><button type="button" disabled={issue.isPending || !quantity || Number(quantity) <= 0} onClick={() => issue.mutate()}>{t('projects.issue')}</button></div>}
      {error && <em className="panel__error">{t(`projects.errors.${error}`, '')}</em>}
    </div>
  );
}

/** Pinning a version into the plan. Only released versions are offered: a draft
 *  would go on changing underneath the project that is being built from it. */
function AddItem({ projectId, onAdded }: { projectId: string; onAdded: () => void }) {
  const { t } = useTranslation();
  const [versionId, setVersionId] = useState('');
  const [units, setUnits] = useState('1');
  const [error, setError] = useState<string | null>(null);

  // Live ones only: an archived specification is not something to add to a
  // project, whatever the list beside it is showing.
  const specs = useQuery({ queryKey: ['specs', 'list', false], queryFn: () => listSpecs() });

  const options = (specs.data ?? []).flatMap((spec) =>
    spec.versions
      .filter((version) => version.status === 'released')
      .map((version) => ({
        id: version.id,
        label: `${spec.name} — v${version.version} (${version.code})`,
      })),
  );

  const add = useMutation({
    mutationFn: () => addItem(projectId, { spec_version_id: versionId, units: Number(units) }),
    onSuccess: onAdded,
    onError: (exc) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  return (
    <form
      className="spec__add-line"
      onSubmit={(event) => {
        event.preventDefault();
        if (versionId) add.mutate();
      }}
    >
      <select value={versionId} onChange={(event) => setVersionId(event.target.value)}>
        <option value="">{t('projects.pickVersion')}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
      <input
        type="number"
        min={1}
        value={units}
        onChange={(event) => setUnits(event.target.value)}
        size={4}
        aria-label={t('projects.units')}
      />
      <button type="submit" disabled={!versionId || add.isPending}>
        {t('common.add')}
      </button>
      {options.length === 0 && <span className="panel__hint">{t('projects.noReleased')}</span>}
      {error && (
        <span className="panel__error">
          {t(`projects.errors.${error}`, t('projects.errors.unknown'))}
        </span>
      )}
    </form>
  );
}
